import { createFormatForDescription as cffd } from '../createFormatForDescription'
import Metadata from '../types/Metadata'
import CurrencyAmount from '../types/util/CurrencyAmount'
import { numberToRankString } from '../util'

import {
  AffectedNodeExplanation,
  createAffectedNodeExplanation,
} from './ExplainAffectedNode'
import explainAmount from './explainAmount'
import {
  Explanation,
  explain,
  explainOptional,
  explainArray,
} from './Explanation'
import lookupErrorCode from './metadataErrorCode'

/**
 * Provides descriptions for transaction metadata.
 *
 * @property affectedNodes - Explanations on the nodes modified by the transaction.
 * @property deliveredAmount - Explains how much currency was sent.
 * @property transactionIndex - Explains the transactionIndex.
 * @property transactionResult - Explains the transactionResult.
 */
export interface MetadataExplanation {
  readonly affectedNodes: Explanation<AffectedNodeExplanation[]>
  readonly deliveredAmount?: Explanation<CurrencyAmount>
  readonly transactionIndex: Explanation<number>
  readonly transactionResult: Explanation<string>
}

/**
 * Creates an explanation for transaction metadata.
 *
 * @param metadata - The metadata to be explained.
 * @returns An explanation of the metadata.
 */
export function createMetadataExplanation(
  metadata: Metadata,
): MetadataExplanation {
  const affectedNodes = explainArray(
    metadata.AffectedNodes,
    createAffectedNodeExplanation,
    'These nodes were affected by the transaction:',
  )

  const transactionIndex = explain(
    metadata.TransactionIndex,
    `This transaction was the ${cffd(
      numberToRankString(metadata.TransactionIndex + 1),
      'transactionIndex',
    )} transaction in the ledger containing it.`,
  )

  const transactionResult = explain(
    metadata.TransactionResult,
    lookupErrorCode(metadata.TransactionResult),
  )

  const deliveredAmount = explainOptional(
    metadata.delivered_amount,
    (amount) => `This transaction sent ${explainAmount(amount)}`,
  )

  return {
    affectedNodes,
    transactionIndex,
    transactionResult,
    ...(deliveredAmount && { deliveredAmount }),
  }
}
